import { ReactNode, useId, useState } from 'react'
import cn from 'classnames'
import {
   FlowIcon,
   TFlowIcon,
} from '@forwardflow/features/campaign-flow/components/ui/FlowIcon'
import { IDelta } from '@forwardflow/features/campaign-flow/utils/format'

/*
 * Stat building blocks (dataviz contract): sentence-case label, semibold value,
 * a signed change vs the previous period coloured by whether that direction is
 * good, and a 12-point trend line in a quiet colour with the latest point in
 * the accent. Every line has a hover readout. Colours: the dashboard's
 * primary #9061DA, success #00d857, the dashboard's red #F37370.
 */

const ACCENT = '#9061DA'
const QUIET = 'rgba(255,255,255,.28)'

export const DeltaBadge = ({
   d,
   vs = 'vs previous period',
}: {
   d: IDelta | null
   vs?: string
}) => {
   if (!d) return null
   return (
      <span
         className="inline-flex items-center gap-1.5 text-xs"
         title={`${d.text} ${vs}`}
      >
         <span
            className={cn(
               'inline-flex items-center gap-0.5 h-5 px-1.5 rounded-md font-semibold tabular-nums',
               d.good === null
                  ? 'bg-white/[0.08] text-[#9ca3af]'
                  : d.good
                    ? 'bg-[#00d857]/[0.14] text-[#00d857]'
                    : 'bg-[#F37370]/[0.14] text-[#F37370]'
            )}
         >
            {d.change !== 0 && (
               <svg
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  aria-hidden="true"
                  className={d.change < 0 ? 'rotate-180' : ''}
               >
                  <path d="M4 1 7 6H1z" fill="currentColor" />
               </svg>
            )}
            {d.text}
         </span>
         <span className="text-[#818181] hidden xl:inline">{vs}</span>
      </span>
   )
}

/**
 * A small trend line. Hover shows the point's date and value. `format` turns a
 * value into text; `labels` names each point.
 */
export const Sparkline = ({
   values,
   labels,
   format = (v) => v.toLocaleString(),
   height = 36,
   accent = ACCENT,
   fill = true,
   testId,
}: {
   values: number[]
   labels?: string[]
   format?: (v: number) => string
   height?: number
   accent?: string
   fill?: boolean
   testId?: string
}) => {
   const [hover, setHover] = useState<number | null>(null)
   const W = 120
   const H = height
   const pad = 3
   const min = Math.min(...values)
   const max = Math.max(...values)
   const span = max - min || 1
   const x = (i: number) =>
      values.length > 1
         ? (i / (values.length - 1)) * (W - pad * 2) + pad
         : W / 2
   const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2)
   const line = values
      .map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
      .join('')
   const area = `${line}L${x(values.length - 1).toFixed(1)},${H}L${x(0).toFixed(1)},${H}Z`
   const last = values.length - 1
   const shown = hover ?? last
   const gid = `sg${useId().replace(/:/g, '')}`
   /* No series yet (live data without a trend): draw nothing rather than a flat line. */
   if (values.length < 2) return null
   return (
      <div
         className="relative w-full"
         style={{ height: H }}
         data-testid={testId}
         onMouseLeave={() => setHover(null)}
      >
         <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="w-full h-full overflow-visible"
            role="img"
            aria-label={`Trend from ${format(values[0])} to ${format(values[last])}`}
            onMouseMove={(e) => {
               const r = e.currentTarget.getBoundingClientRect()
               const i = Math.round(
                  ((e.clientX - r.left) / r.width) * (values.length - 1)
               )
               setHover(Math.max(0, Math.min(last, i)))
            }}
         >
            {fill && (
               <>
                  <defs>
                     <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0" stopColor={accent} stopOpacity=".22" />
                        <stop offset="1" stopColor={accent} stopOpacity="0" />
                     </linearGradient>
                  </defs>
                  <path d={area} fill={`url(#${gid})`} />
               </>
            )}
            <path
               d={line}
               fill="none"
               stroke={QUIET}
               strokeWidth="1.6"
               vectorEffect="non-scaling-stroke"
               strokeLinejoin="round"
               strokeLinecap="round"
            />
            {hover !== null && (
               <line
                  x1={x(hover)}
                  x2={x(hover)}
                  y1="0"
                  y2={H}
                  stroke="rgba(255,255,255,.2)"
                  vectorEffect="non-scaling-stroke"
               />
            )}
         </svg>
         {/* Dots in HTML so they stay round with preserveAspectRatio="none". */}
         <span
            className="absolute w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2 ring-2 ring-[#24252a]"
            style={{
               left: `${(x(shown) / W) * 100}%`,
               top: y(values[shown]),
               background: accent,
            }}
         />
         {hover !== null && (
            <span
               className="absolute z-10 -translate-x-1/2 -translate-y-full -mt-2 whitespace-nowrap rounded-md bg-[#0f1013] border border-white/10 px-2 py-1 text-[11px] shadow-lg pointer-events-none"
               style={{
                  left: `${(x(hover) / W) * 100}%`,
                  top: y(values[hover]) - 6,
               }}
               role="tooltip"
            >
               {labels?.[hover] && (
                  <span className="text-[#9ca3af] mr-1.5">{labels[hover]}</span>
               )}
               <b className="font-semibold">{format(values[hover])}</b>
            </span>
         )}
      </div>
   )
}

/** KPI tile: label, value, change, trend. */
export const StatTile = ({
   icon,
   label,
   value,
   d,
   trend,
   labels,
   format,
   footnote,
   testId,
   onOpen,
}: {
   icon: TFlowIcon
   label: string
   value: string
   d: IDelta | null
   trend: number[]
   labels: string[]
   format?: (v: number) => string
   footnote?: ReactNode
   testId?: string
   /** Makes the tile a link: opens these leads where they live (Conversations). */
   onOpen?: () => void
}) => (
   <div
      className={`rounded-[14px] bg-white/[0.05] border border-white/[0.06] px-4 pt-3.5 pb-3 flex flex-col gap-2 min-w-0${
         onOpen
            ? ' cursor-pointer transition-colors hover:border-[#9061DA]/50 hover:bg-white/[0.07]'
            : ''
      }`}
      data-testid={testId}
      {...(onOpen
         ? {
              onClick: onOpen,
              role: 'link',
              tabIndex: 0,
              'aria-label': `Open ${label} in Conversations`,
              onKeyDown: (e: { key: string; preventDefault: () => void }) => {
                 if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen()
                 }
              },
           }
         : {})}
   >
      <div className="flex items-center gap-2 text-[13px] text-[#9ca3af]">
         <span className="w-6 h-6 rounded-md bg-[#9061DA]/20 text-[#DAC2FF] flex items-center justify-center shrink-0">
            <FlowIcon name={icon} size={13} />
         </span>
         {label}
      </div>
      <div className="flex items-end justify-between gap-3">
         <div className="min-w-0">
            <div className="text-[28px] leading-8 font-semibold">{value}</div>
            <div className="mt-1 h-5">
               <DeltaBadge d={d} />
            </div>
         </div>
         <div className="w-[46%] max-w-[140px] shrink-0">
            <Sparkline values={trend} labels={labels} format={format} />
         </div>
      </div>
      {footnote && (
         <div className="text-xs text-[#818181] border-t border-white/[0.06] pt-2">
            {footnote}
         </div>
      )}
   </div>
)

/** Rate as a short bar on a same-hue track, with a tick at the average. */
export const RateBar = ({
   value,
   average,
   max = 15,
}: {
   value: number
   average: number
   max?: number
}) => {
   const w = Math.min(100, (value / max) * 100)
   const a = Math.min(100, (average / max) * 100)
   return (
      <span
         className="relative block h-1.5 w-full max-w-[96px] rounded-full bg-[#9061DA]/20"
         title={`Your average is ${average.toFixed(1)}%`}
      >
         <span
            className="absolute left-0 top-0 h-1.5 rounded-full bg-[#9061DA]"
            style={{ width: `${w}%` }}
         />
         <span
            className="absolute -top-[3px] w-[2px] h-3 rounded bg-white/70"
            style={{ left: `calc(${a}% - 1px)` }}
         />
      </span>
   )
}

/** Share bar: part of a whole on a same-hue track. */
export const ShareBar = ({ part, whole }: { part: number; whole: number }) => (
   <span className="relative block h-1 w-full max-w-[96px] rounded-full bg-white/[0.08]">
      <span
         className="absolute left-0 top-0 h-1 rounded-full bg-[#DAC2FF]/70"
         style={{
            width: `${whole ? Math.min(100, (part / whole) * 100) : 0}%`,
         }}
      />
   </span>
)
